import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { db } from '../firebase-config';
import { doc, setDoc } from 'firebase/firestore';
import { Geolocation } from '@capacitor/geolocation';
import { NativeSettings, AndroidSettings } from 'capacitor-native-settings';

const LocationContext = createContext();

export const useLocation = () => {
    return useContext(LocationContext);
};

export const LocationProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [locationCoords, setLocationCoords] = useState(null);
    const [locationName, setLocationName] = useState(null); // e.g., { city: 'Hyderabad', state: 'Telangana' }
    const [locationError, setLocationError] = useState('');
    const [isDetecting, setIsDetecting] = useState(false);

    // This is the main function that will be called from our components
    const detectAndSaveLocation = async () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by this browser.');
            return;
        }

        setIsDetecting(true);
        setLocationError('');

        // navigator.geolocation.getCurrentPosition(
        //     async (position) => {
        //         const { latitude, longitude } = position.coords;
        //         setLocationCoords({ latitude, longitude });

        try {
        // --- STEP 1: PERMISSION CHECK ---
        let permissionStatus;
        try {
            permissionStatus = await Geolocation.checkPermissions();
        } catch (e) {
            // Check for the "GPS OFF" error immediately
            if (e.message?.includes("Location services are not enabled") || e.code === 'OS-PLUG-GLOC-0007') {
                await handleGpsOff();
                return; 
            }
            throw e;
        }

        // --- STEP 2: REQUEST PERMISSION IF NOT GRANTED ---
        if (permissionStatus.location !== 'granted' && permissionStatus.coarseLocation !== 'granted') {
            const showRationale = window.confirm("Ai News would like to access your location to show relevant local news.");
            if (showRationale) {
                permissionStatus = await Geolocation.requestPermissions();
            } else {
                setLocationError("Location permission denied.");
                setIsDetecting(false);
                return;
            }
        }

        // Final check after request
        if (permissionStatus.location !== 'granted' && permissionStatus.coarseLocation !== 'granted') {
            setLocationError("Location permission was denied.");
            setIsDetecting(false);
            return;
        }

        // --- STEP 3: GET COORDINATES ---
        try {
            const coordinates = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 15000, // 15 seconds limit
            });
            await processCoordinates(coordinates);
        } catch (posError) {
            if (posError.message?.includes("Location services are not enabled")) {
                await handleGpsOff();
            } else {
                console.error("Position Error:", posError);
                setLocationError("GPS signal too weak. Please try again in an open area.");
                setIsDetecting(false);
            }
        }

    } catch (error) {
        console.error("Main Geolocation error:", error);
        setLocationError("An error occurred. Please check your GPS settings.");
        setIsDetecting(false);
    }
};

// At the top, we need to access the Cordova plugin
// Since it's a Cordova plugin in a Capacitor project, we access it via 'window'
const requestNativeGps = () => {
    return new Promise((resolve, reject) => {
        if (window.cordova && window.cordova.plugins.locationAccuracy) {
            window.cordova.plugins.locationAccuracy.request(
                (success) => resolve(success),
                (error) => reject(error),
                window.cordova.plugins.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY
            );
        } else {
            reject("Plugin not found");
        }
    });
};

// --- FIXED HELPER FUNCTION ---
// Update your handleGpsOff function:
const handleGpsOff = async () => {
    try {
        // This will show the "Automatic" Google Play Services popup
        await requestNativeGps();
        // If user clicks "OK", try to get location again
        const coordinates = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
        });
        await processCoordinates(coordinates);
    } catch (err) {
        console.error("GPS Request failed", err);
        setLocationError("Please enable GPS manually to see local news.");
        setIsDetecting(false);
    }
};
    
    // Helper function to process coordinates and prevent code duplication
    const processCoordinates = async (coordinates) => {
        const { latitude, longitude } = coordinates.coords;
        setLocationCoords({ latitude, longitude });
        await reverseGeocodeAndSave({ latitude, longitude });
    };

    const reverseGeocodeAndSave = async ({ latitude, longitude }) => {
        try {
            // Make sure your Google Maps API Key is correct here!
            const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyANMC2nyUb9egWGq9MCzq927TMyr5plFOA`;

            const geoResponse = await axios.get(geocodeUrl);
            
            // This check is crucial for debugging Google API errors
        if (!geoResponse.data || geoResponse.data.status !== 'OK') {
            console.error("Google Geocoding API Error:", geoResponse.data);
            // This will give a much more useful error message
            throw new Error(`Google API Error: ${geoResponse.data.status}. ${geoResponse.data.error_message || 'Check your API key, restrictions, and billing.'}`);
        }

        const address = geoResponse.data.results[0].address_components;
        // 💡 FIX: Prioritize District (administrative_area_level_2) over Locality/City
        const districtComponent = address.find(c => c.types.includes('administrative_area_level_2'));
        const cityComponent = address.find(c => c.types.includes('locality')); // City/Nagar/Locality
        const stateComponent = address.find(c => c.types.includes('administrative_area_level_1'));
        
        // Use District name if available, otherwise fallback to City/Locality
        const city = districtComponent?.long_name || cityComponent?.long_name;
        const state = stateComponent?.long_name;
        
        if (!city || !state) {
            throw new Error("Could not parse city/state from geocoding response.");
        }

        const detectedLocation = { city, state };
        setLocationName(detectedLocation);
        setLocationError(''); // Clear any previous errors

        if (currentUser) {
            await setDoc(doc(db, 'users', currentUser.uid), { location: detectedLocation }, { merge: true });
        }
    } catch (error) {
        console.error("Geocoding or save error:", error);
        // This will now set the error message correctly.
        setLocationError('Could not determine location name.'); 
    } finally {
        setIsDetecting(false);
    }
};

    const value = { locationCoords, locationName, isDetecting, detectAndSaveLocation, locationError };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};